import { Permission } from '@up2stay/up2promo-api-client';
import {
  BarChart3,
  Bell,
  CalendarDays,
  ContactRound,
  CreditCard,
  FileStack,
  FileText,
  Gauge,
  Crosshair,
  Inbox,
  LayoutDashboard,
  type LucideIcon,
  MapPin,
  MessageSquareText,
  Package,
  Plug,
  Settings,
  Star,
  UserCircle,
  Users,
} from 'lucide-react';

import type { FeatureKey } from '@/core/authz/entitlement-catalog';
import type {
  NavGroup,
  NavItem,
  NavLink,
  SidebarNavigationData,
  SidebarUser,
} from '@/core/components/navigation/types';
import { shellPaths } from '@/core/router/app-paths';

export type ShellNavLinkDef = {
  kind: 'link';
  titleKey: string;
  path: string;
  icon: LucideIcon;
  requiredPermission?: Permission | Permission[];
  /** Bottom-tab roots — shown in sidebar but omitted from the More hub. */
  mobileTabRoot?: boolean;
  /**
   * Ops-critical destinations: lead the More hub Ops strip and are omitted
   * from later domain sections (avoids duplicate rows).
   */
  moreOpsLane?: boolean;
  showBadge?: boolean;
  requiredFeature?: FeatureKey;
};

export type ShellNavSubLinkDef = {
  titleKey: string;
  path: string;
  icon: LucideIcon;
  requiredPermission?: Permission | Permission[];
  /** Extra path prefixes that keep this item active (detail routes, etc.). */
  activePrefixes?: string[];
  /**
   * Path prefixes that must NOT count as active (e.g. Alle Leads vs
   * `/tabs/leads/events/*`).
   */
  excludePrefixes?: string[];
  requiredFeature?: FeatureKey;
};

export type ShellNavFeatureGroupDef = {
  kind: 'group';
  titleKey: string;
  icon: LucideIcon;
  requiredPermission?: Permission | Permission[];
  moreOpsLane?: boolean;
  /**
   * Bottom-tab roots — shown in sidebar as a group but omitted from the More
   * hub (same as link `mobileTabRoot`).
   */
  mobileTabRoot?: boolean;
  requiredFeature?: FeatureKey;
  children: ShellNavSubLinkDef[];
};

export type ShellNavDomainItem = ShellNavLinkDef | ShellNavFeatureGroupDef;

export type ShellNavDomainDef = {
  titleKey: string;
  /**
   * More-hub title when ops-lane items were pulled into the Ops strip
   * (keeps sidebar “Locations & settings” while admin remainder stays clear).
   */
  moreRemainderTitleKey?: string;
  items: ShellNavDomainItem[];
};

export type MobileMoreHubSubItem = {
  titleKey: string;
  path: string;
  icon: LucideIcon;
  requiredFeature?: FeatureKey;
};

export type MobileMoreHubLinkItem = {
  kind: 'link';
  titleKey: string;
  path: string;
  icon: LucideIcon;
  showBadge?: boolean;
  requiredFeature?: FeatureKey;
};

export type MobileMoreHubGroupItem = {
  kind: 'group';
  titleKey: string;
  icon: LucideIcon;
  requiredFeature?: FeatureKey;
  children: MobileMoreHubSubItem[];
};

export type MobileMoreHubItem = MobileMoreHubLinkItem | MobileMoreHubGroupItem;

export type MobileMoreHubSection = {
  titleKey: string;
  items: MobileMoreHubItem[];
};

/** Stable Ops-strip order (Work collapsibles + Workspace locations). */
const MORE_OPS_LANE_ORDER = [
  'products',
  'locations',
  'reputation',
  'feedback',
] as const;

export const SHELL_NAV_ACCOUNT_ITEMS: ShellNavLinkDef[] = [
  {
    kind: 'link',
    titleKey: 'notifications',
    path: shellPaths.moreUserNotifications,
    icon: Bell,
    showBadge: true,
  },
];

export const SHELL_NAV_DOMAINS: ShellNavDomainDef[] = [
  {
    titleKey: 'navDomainWork',
    items: [
      {
        kind: 'link',
        titleKey: 'dashboard',
        path: shellPaths.home,
        icon: LayoutDashboard,
        requiredPermission: Permission.WORKSPACE_READ,
        mobileTabRoot: true,
      },
      {
        kind: 'link',
        titleKey: 'profiles',
        path: shellPaths.profiles,
        icon: UserCircle,
        requiredPermission: Permission.PROFILES_READ,
        mobileTabRoot: true,
      },
      {
        kind: 'group',
        titleKey: 'leads',
        icon: Users,
        requiredPermission: [Permission.LEADS_READ, Permission.EVENTS_READ],
        mobileTabRoot: true,
        children: [
          {
            titleKey: 'leadsAll',
            path: shellPaths.leads,
            icon: Users,
            requiredPermission: Permission.LEADS_READ,
            excludePrefixes: [shellPaths.leadsEvents],
          },
          {
            titleKey: 'leadsEvents',
            path: shellPaths.leadsEvents,
            icon: CalendarDays,
            requiredPermission: Permission.EVENTS_READ,
            activePrefixes: [shellPaths.leadsEvents],
            requiredFeature: 'feature.lead_events',
          },
        ],
      },
      {
        kind: 'link',
        titleKey: 'products',
        path: shellPaths.moreProducts,
        icon: Package,
        requiredPermission: Permission.PRODUCTS_READ,
        moreOpsLane: true,
      },
      {
        kind: 'group',
        titleKey: 'feedback',
        icon: MessageSquareText,
        requiredPermission: Permission.FEEDBACK_READ,
        moreOpsLane: true,
        requiredFeature: 'feature.feedback',
        children: [
          {
            titleKey: 'feedbackHome',
            path: shellPaths.feedbackHome,
            icon: MessageSquareText,
          },
          {
            titleKey: 'feedbackActivity',
            path: shellPaths.feedbackActivity,
            icon: Inbox,
            activePrefixes: [
              shellPaths.feedbackInbox,
              shellPaths.feedbackInvites,
            ],
          },
          {
            titleKey: 'feedbackLibrary',
            path: shellPaths.feedbackLibrary,
            icon: FileText,
            activePrefixes: [
              shellPaths.feedbackForms,
              shellPaths.feedbackMessages,
            ],
          },
        ],
      },
      {
        kind: 'group',
        titleKey: 'reputation',
        icon: Gauge,
        requiredPermission: Permission.REPUTATION_READ,
        moreOpsLane: true,
        requiredFeature: 'feature.reputation',
        children: [
          {
            titleKey: 'reputationOverview',
            path: shellPaths.reputationDashboard,
            icon: Gauge,
          },
          {
            titleKey: 'reviews',
            path: shellPaths.reputationInbox,
            icon: Star,
          },
          {
            titleKey: 'reputationAnalytics',
            path: shellPaths.reputationAnalytics,
            icon: BarChart3,
            requiredFeature: 'feature.advanced_analytics',
          },
          {
            titleKey: 'reputationCompetitors',
            path: shellPaths.reputationCompetitors,
            icon: Crosshair,
            requiredFeature: 'feature.competitor_benchmark',
          },
          {
            titleKey: 'reputationSetup',
            path: shellPaths.reputationSetup,
            icon: Settings,
            requiredPermission: Permission.REPUTATION_CONFIGURE,
          },
        ],
      },
    ],
  },
  {
    titleKey: 'navDomainPeople',
    items: [
      {
        kind: 'link',
        titleKey: 'people',
        path: shellPaths.morePeople,
        icon: ContactRound,
        requiredPermission: [Permission.MEMBERS_READ, Permission.TEAMS_READ],
      },
      {
        kind: 'link',
        titleKey: 'profileTemplates',
        path: shellPaths.moreProfileTemplates,
        icon: FileStack,
        requiredPermission: Permission.PROFILES_MANAGE_TEMPLATES,
      },
    ],
  },
  {
    titleKey: 'navDomainWorkspace',
    moreRemainderTitleKey: 'navDomainWorkspaceAdmin',
    items: [
      {
        kind: 'link',
        titleKey: 'locations',
        path: shellPaths.moreWorkspaceLocations,
        icon: MapPin,
        requiredPermission: Permission.LOCATIONS_READ,
        moreOpsLane: true,
      },
      {
        kind: 'link',
        titleKey: 'integrations',
        path: shellPaths.moreWorkspaceIntegrations,
        icon: Plug,
        requiredPermission: Permission.INTEGRATIONS_READ,
        requiredFeature: 'feature.integrations',
      },
      {
        kind: 'link',
        titleKey: 'billing',
        path: shellPaths.moreWorkspaceBilling,
        icon: CreditCard,
        requiredPermission: Permission.BILLING_READ,
      },
      {
        kind: 'link',
        titleKey: 'workspaceSettings',
        path: shellPaths.moreWorkspaceSettings,
        icon: Settings,
        requiredPermission: Permission.WORKSPACE_MANAGE_SETTINGS,
      },
    ],
  },
];

function hasRequiredPermission(
  required: Permission | Permission[] | undefined,
  can: (permission: Permission) => boolean,
): boolean {
  if (!required) return true;
  const permissions = Array.isArray(required) ? required : [required];
  return permissions.some(can);
}

function isLinkVisible(
  item: ShellNavLinkDef,
  can: (permission: Permission) => boolean,
): boolean {
  return hasRequiredPermission(item.requiredPermission, can);
}

function isOpsLaneItem(item: ShellNavDomainItem): boolean {
  return item.moreOpsLane === true;
}

function filterSubLinks(
  children: ShellNavSubLinkDef[],
  can: (permission: Permission) => boolean,
): ShellNavSubLinkDef[] {
  return children.filter((child) =>
    hasRequiredPermission(child.requiredPermission, can),
  );
}

/**
 * Child nav rows inherit the parent group gate when they have no more-specific
 * feature of their own (e.g. Reputation → Overview inherits Plus; Competitors
 * keeps Teams via its own `requiredFeature`).
 */
export function resolveNavRequiredFeature(
  own: FeatureKey | undefined,
  parent?: FeatureKey,
): FeatureKey | undefined {
  return own ?? parent;
}

/**
 * Drops items the role cannot reach and narrows groups to their visible
 * children, so a group never renders with an empty or unreachable child list.
 */
function filterDomainItems(
  items: ShellNavDomainItem[],
  can: (permission: Permission) => boolean,
): ShellNavDomainItem[] {
  return items.flatMap<ShellNavDomainItem>((item) => {
    if (item.kind === 'group') {
      if (!hasRequiredPermission(item.requiredPermission, can)) return [];
      const children = filterSubLinks(item.children, can);
      if (children.length === 0) return [];
      return [{ ...item, children }];
    }
    return isLinkVisible(item, can) ? [item] : [];
  });
}
function translate(
  dictNav: Record<string, string> | undefined,
  locale: string,
  key: string,
): string {
  if (dictNav?.[key]) return dictNav[key];

  const fallbacks: Record<string, Record<string, string>> = {
    en: {
      navDomainWork: 'Work',
      navDomainPeople: 'Team',
      navDomainWorkspace: 'Locations & settings',
      navDomainWorkspaceAdmin: 'Workspace',
      navOps: 'Operations',
      dashboard: 'Status Board',
      products: 'Products',
      leads: 'Leads',
      leadsAll: 'All leads',
      leadsEvents: 'Events',
      profiles: 'Profiles',
      teams: 'Teams',
      profileTemplates: 'Profile templates',
      people: 'People',
      integrations: 'Integrations',
      reviews: 'Reviews',
      reputation: 'Reputation',
      reputationOverview: 'Overview',
      reputationAnalytics: 'Analytics',
      reputationCompetitors: 'Competitors',
      reputationSetup: 'Setup',
      feedback: 'Feedback',
      feedbackHome: 'Home',
      feedbackActivity: 'Activity',
      feedbackLibrary: 'Library',
      feedbackInbox: 'Inbox',
      feedbackInvites: 'Sent',
      feedbackForms: 'Forms',
      feedbackMessages: 'Messages',
      locations: 'Locations',
      workspaceSettings: 'Workspace settings',
      notifications: 'Notifications',
      billing: 'Billing',
    },
    de: {
      navDomainWork: 'Arbeit',
      navDomainPeople: 'Team',
      navDomainWorkspace: 'Standorte & Einstellungen',
      navDomainWorkspaceAdmin: 'Arbeitsbereich',
      navOps: 'Betrieb',
      dashboard: 'Statusboard',
      products: 'Produkte',
      leads: 'Leads',
      leadsAll: 'Alle Leads',
      leadsEvents: 'Events',
      profiles: 'Profile',
      teams: 'Teams',
      profileTemplates: 'Profilvorlagen',
      people: 'Personen',
      integrations: 'Integrationen',
      reviews: 'Bewertungen',
      reputation: 'Reputation',
      reputationOverview: 'Übersicht',
      reputationAnalytics: 'Analysen',
      reputationCompetitors: 'Wettbewerber',
      reputationSetup: 'Einrichtung',
      feedback: 'Feedback',
      feedbackHome: 'Start',
      feedbackActivity: 'Aktivität',
      feedbackLibrary: 'Vorlagen',
      feedbackInbox: 'Posteingang',
      feedbackInvites: 'Gesendet',
      feedbackForms: 'Fragebögen',
      feedbackMessages: 'E-Mail-Texte',
      locations: 'Standorte',
      workspaceSettings: 'Einstellungen',
      notifications: 'Benachrichtigungen',
      billing: 'Abrechnung',
    },
  };

  return fallbacks[locale]?.[key] ?? fallbacks.en?.[key] ?? key;
}

function toSidebarNavItem(
  item: ShellNavDomainItem,
  t: (key: string) => string,
): NavItem[] {
  if (item.kind === 'group') {
    // Keep feature groups collapsible in the desktop sidebar (and icon-rail
    // dropdowns when collapsed) — same progressive disclosure as More hub Ops.
    return [
      {
        title: t(item.titleKey),
        icon: item.icon,
        requiredFeature: item.requiredFeature,
        items: item.children.map((child) => ({
          title: t(child.titleKey),
          url: child.path,
          icon: child.icon,
          activePrefixes: child.activePrefixes,
          excludePrefixes: child.excludePrefixes,
          requiredFeature: resolveNavRequiredFeature(
            child.requiredFeature,
            item.requiredFeature,
          ),
        })),
      },
    ];
  }

  return [
    {
      title: t(item.titleKey),
      url: item.path,
      icon: item.icon,
      requiredFeature: item.requiredFeature,
    },
  ];
}

export function buildSidebarNavGroups(options: {
  locale: string;
  dictNav?: Record<string, string>;
  hasWorkspace: boolean;
  can: (permission: Permission) => boolean;
}): NavGroup[] {
  if (!options.hasWorkspace) return [];

  const t = (key: string) => translate(options.dictNav, options.locale, key);

  return SHELL_NAV_DOMAINS.map((domain) => {
    const visibleItems = filterDomainItems(domain.items, options.can);
    if (visibleItems.length === 0) return null;

    const navItems = visibleItems.flatMap((item) => toSidebarNavItem(item, t));
    if (navItems.length === 0) return null;

    return {
      title: t(domain.titleKey),
      items: navItems,
    };
  }).filter((group): group is NavGroup => group != null);
}

function toMoreHubItem(
  item: ShellNavDomainItem,
): MobileMoreHubItem | MobileMoreHubItem[] | null {
  if (item.kind === 'group') {
    // Bottom-tab feature groups stay out of More (Leads → Alle Leads / Events).
    if (item.mobileTabRoot) return null;
    // Flatten children under the section heading — avoids Group→Group
    // accordion double-title on the More hub.
    return item.children.map((child): MobileMoreHubLinkItem => ({
      kind: 'link',
      titleKey: child.titleKey,
      path: child.path,
      icon: child.icon,
      requiredFeature: resolveNavRequiredFeature(
        child.requiredFeature,
        item.requiredFeature,
      ),
    }));
  }

  if (item.mobileTabRoot) return null;

  return {
    kind: 'link',
    titleKey: item.titleKey,
    path: item.path,
    icon: item.icon,
    requiredFeature: item.requiredFeature,
  };
}

function toMoreHubOpsItem(item: ShellNavDomainItem): MobileMoreHubItem | null {
  if (item.kind === 'group') {
    // Under the Ops strip, keep a single accordion per feature group.
    return {
      kind: 'group',
      titleKey: item.titleKey,
      icon: item.icon,
      requiredFeature: item.requiredFeature,
      children: item.children.map((child) => ({
        titleKey: child.titleKey,
        path: child.path,
        icon: child.icon,
        requiredFeature: resolveNavRequiredFeature(
          child.requiredFeature,
          item.requiredFeature,
        ),
      })),
    };
  }

  if (item.mobileTabRoot) return null;

  return {
    kind: 'link',
    titleKey: item.titleKey,
    path: item.path,
    icon: item.icon,
    requiredFeature: item.requiredFeature,
  };
}

export function buildMoreHubAccountItems(
  can: (permission: Permission) => boolean,
): MobileMoreHubLinkItem[] {
  return SHELL_NAV_ACCOUNT_ITEMS.filter((item) => isLinkVisible(item, can)).map(
    (item) => ({
      kind: 'link' as const,
      titleKey: item.titleKey,
      path: item.path,
      icon: item.icon,
      showBadge: item.showBadge,
    }),
  );
}

/**
 * Status Board ops directory for the More hub — Products, Locations,
 * Reputation (permission-filtered), ahead of account/theme chrome.
 */
export function buildMoreHubOpsSection(
  can: (permission: Permission) => boolean,
): MobileMoreHubSection | null {
  const byKey = new Map<string, MobileMoreHubItem>();

  for (const domain of SHELL_NAV_DOMAINS) {
    for (const item of filterDomainItems(domain.items, can)) {
      if (!isOpsLaneItem(item)) continue;
      const mapped = toMoreHubOpsItem(item);
      if (!mapped) continue;
      byKey.set(item.titleKey, mapped);
    }
  }

  const items = MORE_OPS_LANE_ORDER.map((key) => byKey.get(key)).filter(
    (item): item is MobileMoreHubItem => item != null,
  );

  if (items.length === 0) return null;

  return {
    titleKey: 'navOps',
    items,
  };
}

export function buildMoreHubDomainSections(
  can: (permission: Permission) => boolean,
): MobileMoreHubSection[] {
  return SHELL_NAV_DOMAINS.map((domain) => {
    const visibleItems = filterDomainItems(domain.items, can);
    const remainderItems = visibleItems.filter((item) => !isOpsLaneItem(item));
    const items = remainderItems
      .flatMap((item) => {
        const mapped = toMoreHubItem(item);
        if (!mapped) return [];
        return Array.isArray(mapped) ? mapped : [mapped];
      })
      .filter(Boolean);

    if (items.length === 0) return null;

    const pulledOpsIntoStrip = visibleItems.some(isOpsLaneItem);

    return {
      titleKey:
        pulledOpsIntoStrip && domain.moreRemainderTitleKey
          ? domain.moreRemainderTitleKey
          : domain.titleKey,
      items,
    };
  }).filter((section): section is MobileMoreHubSection => section != null);
}

export function isMobileMoreHubGroup(
  item: MobileMoreHubItem,
): item is MobileMoreHubGroupItem {
  return item.kind === 'group';
}

export function createNavigationData(
  locale: string,
  user: SidebarUser,
  isWorkspaceScope: boolean = false,
  dictNav?: Record<string, string>,
  can: (permission: Permission) => boolean = () => false,
): SidebarNavigationData {
  return {
    user,
    navGroups: buildSidebarNavGroups({
      locale,
      dictNav,
      hasWorkspace: isWorkspaceScope,
      can,
    }),
  };
}

export function createMobileMoreHubSections(
  can: (permission: Permission) => boolean,
): {
  accountItems: MobileMoreHubLinkItem[];
  opsSection: MobileMoreHubSection | null;
  domainSections: MobileMoreHubSection[];
} {
  return {
    accountItems: buildMoreHubAccountItems(can),
    opsSection: buildMoreHubOpsSection(can),
    domainSections: buildMoreHubDomainSections(can),
  };
}
